import {
  FormControlLabel,
  Radio,
  RadioGroup,
  type RadioGroupProps,
} from "@mui/material";
import { useIntl } from "react-intl";

import { getTravellerAgeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { getTravelerTypeOfAge } from "@/features/travellers/utils/transformTraveler";
import type { Traveller } from "@/types/types";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = RadioGroupProps & {
  travelDate: Date;
  selectedTravellers: Traveller[];
};

export const MainTravellerRadioButtons = ({
  travelDate,
  selectedTravellers,
  ...props
}: Props) => {
  const intl = useIntl();

  return (
    <RadioGroup {...props}>
      {selectedTravellers.map((traveller) => {
        const ageAtTravelDate = getTravellerAgeAtTravelDate(
          traveller.birthDate,
          travelDate,
        );
        const ageGroupAtTravelDate = getTravelerTypeOfAge(ageAtTravelDate);

        const currentAge = getAgeFromDateOfBirth(traveller.birthDate);
        const currentAgeGroup = getTravelerTypeOfAge(currentAge);

        return (
          <FormControlLabel
            key={traveller.id}
            value={traveller.id}
            control={<Radio />}
            disabled={ageGroupAtTravelDate !== "adult"}
            label={
              <div className="flex items-center gap-2">
                <div className="grow" data-sentry-mask>
                  <div className="font-medium text-text-title">
                    {traveller.firstName} {traveller.lastName}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {intl.formatMessage({ id: "hotels.overview.age" })}{" "}
                    {currentAge}
                  </div>
                  {ageGroupAtTravelDate !== currentAgeGroup ? (
                    <div className="mt-1 text-xs text-semantic-warning-strong">
                      {intl.formatMessage(
                        { id: "travellers.ageValidationError" },
                        {
                          ageGroupAtTravelDate,
                          age: ageAtTravelDate,
                        },
                      )}
                    </div>
                  ) : null}
                </div>
                {props.value === traveller.id ? (
                  <div className="shrink-0 text-xs font-medium">
                    {intl.formatMessage({ id: "travellers.mainTraveller" })}
                  </div>
                ) : null}
              </div>
            }
            sx={{
              display: "flex",
              gap: "calc(var(--spacing)*4)",
              m: 0,
              p: 2,
              "&:not(:last-of-type)": {
                borderBottom: "1px solid #E5E5E5",
              },
              ".MuiFormControlLabel-label": {
                width: "100%",
              },
            }}
          />
        );
      })}
    </RadioGroup>
  );
};
